import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { trackEvent, trackPageView } from '../../analytics';
import { useI18n } from '../../i18n/I18nContext';
import { translations } from '../../i18n/translations';

type PageKey = keyof typeof translations.en.pageHeaders;

const SITE_URL = 'https://assetfactory.app';
const INDEX_ROBOTS = 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';
const ROUTE_PAGES: Record<string, PageKey> = {
  '/about': 'about',
  '/business': 'business',
  '/contact': 'contact',
  '/features': 'features',
  '/gallery': 'gallery',
  '/how-it-works': 'how',
  '/plans': 'plans',
  '/request': 'request',
  '/services': 'services',
  '/testimonials': 'testimonials',
};
const OG_LOCALES = { en: 'en_US', es: 'es_MX', hi: 'hi_IN' };

function setMeta(selector: string, value: string): void {
  document.querySelector<HTMLMetaElement>(selector)?.setAttribute('content', value);
}

const SeoAnalytics: React.FC = () => {
  const location = useLocation();
  const { language, content } = useI18n();

  useEffect(() => {
    const isHome = location.pathname === '/';
    const pageKey = ROUTE_PAGES[location.pathname];
    const isKnownRoute = isHome || Boolean(pageKey);
    const page = pageKey ? content.pageHeaders[pageKey] : undefined;
    const title = isHome ? content.meta.title : page ? `${page.title} | AssetFactory` : content.meta.title;
    const description = isHome ? content.meta.description : page?.subtitle || content.meta.description;
    const canonicalPath = isKnownRoute ? location.pathname : '/';
    const canonicalUrl = `${SITE_URL}${canonicalPath === '/' ? '/' : canonicalPath}`;
    const pagePath = `${location.pathname}${location.search}`;

    document.title = title;
    document.documentElement.lang = language;
    document.querySelector<HTMLLinkElement>('link[rel="canonical"]')?.setAttribute('href', canonicalUrl);
    setMeta('meta[name="description"]', description);
    setMeta('meta[name="robots"]', isKnownRoute ? INDEX_ROBOTS : 'noindex, follow');
    setMeta('meta[name="googlebot"]', isKnownRoute ? INDEX_ROBOTS : 'noindex, follow');
    setMeta('meta[property="og:title"]', title);
    setMeta('meta[property="og:description"]', description);
    setMeta('meta[property="og:url"]', canonicalUrl);
    setMeta('meta[property="og:locale"]', OG_LOCALES[language]);
    setMeta('meta[name="twitter:title"]', title);
    setMeta('meta[name="twitter:description"]', description);

    trackPageView({
      pageTitle: title,
      pageLocation: window.location.href,
      pagePath,
      language,
    });
  }, [content, language, location.pathname, location.search]);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const element = event.target instanceof Element
        ? event.target.closest<HTMLElement>('a, button, [data-analytics-event]')
        : null;
      if (!element) return;

      const customEvent = element.dataset.analyticsEvent;
      if (customEvent) {
        trackEvent(customEvent, {
          element_text: element.textContent?.trim().replace(/\s+/g, ' ').slice(0, 100),
          page_path: window.location.pathname,
        });
        return;
      }

      if (element instanceof HTMLAnchorElement) {
        const rawHref = element.getAttribute('href') || '';
        const linkType = rawHref.startsWith('mailto:')
          ? 'email'
          : rawHref.startsWith('tel:')
            ? 'phone'
            : element.origin !== window.location.origin
              ? 'outbound'
              : 'internal';

        trackEvent(linkType === 'email' ? 'contact_click' : 'navigation_click', {
          link_type: linkType,
          link_text: element.textContent?.trim().replace(/\s+/g, ' ').slice(0, 100),
          link_url: rawHref.slice(0, 300),
          page_path: window.location.pathname,
        });
      }
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  return null;
};

export default SeoAnalytics;
